import { useEffect, useState } from "react";
import "@/App.css";
import "@/i18n";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import logger from "@/utils/logger";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Menu, X, Phone, Mail, MapPin, Instagram, Facebook, Globe } from "lucide-react";

// Pages
import HomePage from "@/pages/HomePage";
import MenuPage from "@/pages/MenuPage";
import GalleryPage from "@/pages/GalleryPage";
import BookingPage from "@/pages/BookingPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Language Toggle Component
const LanguageToggle = ({ isScrolled }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  const toggleLanguage = () => {
    const newLang = currentLang === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  return (
    <button
      data-testid="language-toggle-btn"
      onClick={toggleLanguage}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition-all duration-200 text-sm font-medium hover:scale-105 ${
        isScrolled
          ? "border-[#3D405B]/30 text-[#3D405B] hover:bg-[#3D405B]/10"
          : "border-white/40 text-white hover:bg-white/15"
      }`}
      aria-label="Toggle language"
    >
      <Globe className="w-4 h-4" />
      <span>{currentLang === 'en' ? 'FR' : 'EN'}</span>
    </button>
  );
};

// Navigation Component
const Navigation = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: t("nav.home") },
    { path: "/menu", label: t("nav.menu") },
    { path: "/gallery", label: t("nav.gallery") },
    { path: "/booking", label: t("nav.bookNow") },
    { path: "/about", label: t("nav.about") },
    { path: "/contact", label: t("nav.contact") },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <nav
        data-testid="main-navigation"
        className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md py-3"
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2"
          >
            <img
              src="/logo.svg"
              alt="Djohomi Foods"
              className="w-10 h-10 object-contain"
            />
            <span className="text-xl font-bold font-['Playfair_Display'] text-[#3D405B]">
              Djohomi Foods
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) =>
              link.path === "/booking" ? (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid="nav-link-booking"
                  className="nav-cta-btn"
                >
                  {link.label}
                </Link>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path === '/' ? 'home' : link.path.slice(1)}`}
                  className={`nav-link text-[#3D405B] ${isActive(link.path) ? "font-semibold" : ""}`}
                >
                  {link.label}
                </Link>
              )
            )}
            <LanguageToggle isScrolled={true} />
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-3 md:hidden">
            <LanguageToggle isScrolled={true} />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="text-[#3D405B]" />
              ) : (
                <Menu className="text-[#3D405B]" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileMenuOpen(false)}
              data-testid={`mobile-nav-link-${link.path === '/' ? 'home' : link.path.slice(1)}`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </>
  );
};

// Footer Component
const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer data-testid="main-footer" className="bg-[#3D405B] text-[#F4F1DE] py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img
                src="/logo.svg"
                alt="Djohomi Foods"
                className="w-10 h-10 object-contain"
              />
              <span className="text-xl font-bold font-['Playfair_Display']">
                Djohomi Foods
              </span>
            </div>
            <p className="text-[#F4F1DE]/70 text-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4 text-[#F2CC8F]">{t("footer.quickLinks")}</h4>
            <div className="flex flex-col gap-2">
              <Link to="/menu" className="footer-link text-sm">{t("footer.ourMenu")}</Link>
              <Link to="/gallery" className="footer-link text-sm">{t("footer.gallery")}</Link>
              <Link to="/booking" className="footer-link text-sm">{t("footer.bookNow")}</Link>
              <Link to="/about" className="footer-link text-sm">{t("footer.aboutUs")}</Link>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-4 text-[#F2CC8F]">{t("footer.services")}</h4>
            <div className="flex flex-col gap-2 text-sm text-[#F4F1DE]/70">
              <span>{t("footer.weddingCatering")}</span>
              <span>{t("footer.corporateEvents")}</span>
              <span>{t("footer.traditionalCeremonies")}</span>
              <span>{t("footer.privateDinners")}</span>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 text-[#F2CC8F]">{t("footer.contactUs")}</h4>
            <div className="flex flex-col gap-3 text-sm">
              <a href="tel:+13437778483" className="footer-link flex items-center gap-2">
                <Phone className="w-4 h-4" /> (343) 777-8483
              </a>
              <a href="mailto:info@djohomifoods.com" className="footer-link flex items-center gap-2">
                <Mail className="w-4 h-4" /> info@djohomifoods.com
              </a>
              <span className="flex items-center gap-2 text-[#F4F1DE]/70">
                <MapPin className="w-4 h-4" /> Ottawa, Ontario, Canada
              </span>
            </div>
            <div className="flex gap-4 mt-4">
              <a href="#" className="text-[#F4F1DE]/70 hover:text-[#F2CC8F] transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="text-[#F4F1DE]/70 hover:text-[#F2CC8F] transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-[#F4F1DE]/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-[#F4F1DE]/60">
            {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Layout wrapper for public pages
const PublicLayout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  if (isAdminRoute) {
    return children;
  }

  return (
    <>
      <Navigation />
      <main>{children}</main>
      <Footer />
    </>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  const phoneNumber = "13437778483";
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent("Hello! I'm interested in your catering services.")}`;

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-floating-btn"
      className="fixed bottom-20 right-6 z-50 flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb855] text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
      aria-label="Contact us on WhatsApp"
    >
      <svg className="w-6 h-6 fill-white" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span className="hidden sm:inline font-medium text-sm">Chat with us</span>
    </a>
  );
};

function App() {
  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {
        logger.error("Failed to seed data:", e);
      }
    };
    seedData();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <PublicLayout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard/*" element={<AdminDashboard />} />
          </Routes>
        </PublicLayout>
      </BrowserRouter>
      <WhatsAppButton />
    </div>
  );
}

export default App;
